"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getHumiditySeverity = exports.isHumidityHigh = exports.isHumidityElevated = exports.HUMIDITY_ATTENTION_RELEASE_PERCENT = exports.HUMIDITY_CRITICAL_PERCENT = exports.HUMIDITY_HIGH_PERCENT = exports.HUMIDITY_ATTENTION_PERCENT = exports.HumidityThresholds = exports.HumidityLevel = void 0;
const SensorAlert_1 = require("../SensorAlert");
var HumidityLevel;
(function (HumidityLevel) {
    HumidityLevel["Dry"] = "dry";
    HumidityLevel["Normal"] = "normal";
    HumidityLevel["SlightlyHumid"] = "slightly_humid";
    HumidityLevel["Humid"] = "humid";
    HumidityLevel["VeryHumid"] = "very_humid";
})(HumidityLevel = exports.HumidityLevel || (exports.HumidityLevel = {}));
exports.HumidityThresholds = {
    [HumidityLevel.Dry]: { low: 0, high: 29 },
    [HumidityLevel.Normal]: { low: 30, high: 49 },
    [HumidityLevel.SlightlyHumid]: { low: 50, high: 64 },
    [HumidityLevel.Humid]: { low: 65, high: 79 },
    [HumidityLevel.VeryHumid]: { low: 80, high: 100 },
};
/**
 * ── Attention lines (separate from the label bands above, on purpose) ────────
 *
 * The bands above name a reading ("Slightly Humid"). These numbers decide whether
 * a card turns amber/red, whether an alert is raised, and what counts toward
 * "needs attention". The attention line sits BELOW the "Humid" band's 65
 * deliberately: 60% sustained is the mold-risk line the field team watches. Don't
 * "reconcile" it upward to 65 — that silently stops flagging the condition we are
 * deliberately watching for.
 *
 * Every surface must compare the ROUNDED reading via the helpers below, never
 * the raw float. The UI prints `Math.round(humidity)%`, so testing the raw value
 * let two rooms both displaying "60%" disagree about whether they were flagged
 * (60.4 flagged, 59.9 not). That was a real field report on 2026-08-11: three
 * HVACGOD detectors sat within 0.5% of each other all morning and the flag
 * hopped between rooms. Anything the homeowner can't see cannot be allowed to
 * change what the homeowner is shown.
 */
exports.HUMIDITY_ATTENTION_PERCENT = 60;
/** Second tier — reading is high enough to read as an error, not a warning. */
exports.HUMIDITY_HIGH_PERCENT = 70;
/**
 * Third tier. Anchored on the "Very Humid" band's floor rather than a new invented
 * number, so the alert a homeowner gets and the label they read on the card always
 * describe the same condition — retune the band and the critical alert follows.
 */
exports.HUMIDITY_CRITICAL_PERCENT = exports.HumidityThresholds[HumidityLevel.VeryHumid].low;
/**
 * Release line for `isHumidityElevated`'s hysteresis. Two points of deadband:
 * these readings drift ±0.5% every 5-minute report, so a bare threshold makes
 * the badge flicker on and off for hours while nothing physically changes.
 */
exports.HUMIDITY_ATTENTION_RELEASE_PERCENT = 58;
/**
 * Whether a humidity reading should flag for attention.
 *
 * Pass `wasElevated` (the sensor's previous answer) to engage hysteresis: once
 * flagged, a reading stays flagged until it falls to the release line. Omit it
 * for one-shot classification of an already-aggregated value.
 *
 * Idempotent by design — feeding the previous output back in as `wasElevated`
 * cannot change the answer, so callers may safely keep this state in a ref that
 * a double-invoked render updates twice.
 */
function isHumidityElevated(humidityPercent, wasElevated = false) {
    const displayed = Math.round(humidityPercent);
    return displayed > (wasElevated ? exports.HUMIDITY_ATTENTION_RELEASE_PERCENT : exports.HUMIDITY_ATTENTION_PERCENT);
}
exports.isHumidityElevated = isHumidityElevated;
/** Whether a humidity reading is in the upper (error-severity) tier. */
function isHumidityHigh(humidityPercent) {
    return Math.round(humidityPercent) > exports.HUMIDITY_HIGH_PERCENT;
}
exports.isHumidityHigh = isHumidityHigh;
/**
 * Alert severity for a humidity reading, or `null` when it needs no alert.
 *
 * This is the cloud-side twin of the card classification above, and the reason
 * both live in this file: the dashboard turned a leak detector amber at 60%
 * while firmware only alerted at 70%, so the 60–70% band rendered as a warning
 * with nothing behind it — no row on the Alerts screen, nothing to clear, no
 * notification. Reading both answers off the same constants is what stops that
 * band from reopening.
 *
 * Severities are chosen so `alertSeverityToStatus` reproduces the card colour
 * exactly: Medium → Poor (amber), High/Critical → Error (red).
 *
 * `wasElevated` threads the same hysteresis the card uses, so an alert that is
 * already open holds until the release line instead of clearing at 60.4%.
 */
function getHumiditySeverity(humidityPercent, wasElevated = false) {
    const displayed = Math.round(humidityPercent);
    if (displayed > exports.HUMIDITY_CRITICAL_PERCENT)
        return SensorAlert_1.SensorAlertSeverity.Critical;
    if (displayed > exports.HUMIDITY_HIGH_PERCENT)
        return SensorAlert_1.SensorAlertSeverity.High;
    if (isHumidityElevated(humidityPercent, wasElevated))
        return SensorAlert_1.SensorAlertSeverity.Medium;
    return null;
}
exports.getHumiditySeverity = getHumiditySeverity;
